import { Injectable } from '@angular/core';
import { TenantAuthService } from '../services/tenant-auth.service';
import { CompanyStorage } from './company-storage';
import { PosTerminalStorageExportService } from './pos-terminal-storage-export.service';

// Placeholder write confirmation until admin auth exists: the admin tool asks for it and sends it
// with every setStorage. Anyone with the app bundle can read it.
const STORAGE_WRITE_SECRET = 'password1';

export interface GetStoragePayload {
  key: string;
}

export interface SetStoragePayload {
  key: string;
  // Any JSON; it replaces the stored value as is.
  value: unknown;
  password: string;
  // storageHash of the value the editor started from: the write is refused when it changed since.
  expectedHash?: string;
}

export interface DeleteStoragePayload {
  key: string;
  password: string;
  // storageHash of the value the admin saw: the delete is refused when it changed since.
  expectedHash?: string;
}

export interface ReadStoragePayload {
  // Regular expression (case-insensitive) the keys must match; all keys without it.
  pattern?: string;
}

const PATTERN_MAX = 500;

// FNV-1a over the JSON text; the admin tool computes the same for values taken from an export.
export function storageHash(value: unknown): string {
  const text = JSON.stringify(value ?? null);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16).padStart(8, '0');
}

// Storage editor commands: getStorage reads one company storage key, setStorage replaces its value.
// Services that read a key once at start keep the old value until the app restarts.
@Injectable({ providedIn: 'root' })
export class PosTerminalStorageEditService {
  constructor(
    private _companyStorage: CompanyStorage,
    private _export: PosTerminalStorageExportService,
    private _tenantAuth: TenantAuthService,
  ) {}

  async get(payload: GetStoragePayload) {
    const key = this._key(payload?.key);
    const raw = await this._companyStorage.get(key);
    const value = this._export.toJsonSafe(raw);

    return {
      key,
      exists: raw !== null && raw !== undefined,
      binary: this._export.hasBinary(raw),
      hash: storageHash(value),
      bytes: new TextEncoder().encode(JSON.stringify(value)).length,
      value,
    };
  }

  async set(payload: SetStoragePayload) {
    if (payload?.password !== STORAGE_WRITE_SECRET) {
      throw new Error('Wrong password: storage was not changed');
    }

    const key = this._key(payload.key);
    if (!('value' in payload)) {
      throw new Error('No value to write');
    }

    const current = await this._companyStorage.get(key);
    if (this._export.hasBinary(current)) {
      throw new Error(`"${key}" holds binary data that JSON can not carry: it is not edited remotely`);
    }

    const previousHash = storageHash(this._export.toJsonSafe(current));
    if (payload.expectedHash && payload.expectedHash !== previousHash) {
      throw new Error(`"${key}" changed on the terminal after the editor opened it: reload it and edit again`);
    }

    await this._companyStorage.set(key, payload.value);
    console.warn(`[storage] admin replaced "${key}"`);

    return {
      key,
      created: current === null || current === undefined,
      previousHash,
      hash: storageHash(payload.value),
      bytes: new TextEncoder().encode(JSON.stringify(payload.value ?? null)).length,
    };
  }

  // Removes the key from the company storage. Services that read it once keep their copy until
  // the app restarts.
  async delete(payload: DeleteStoragePayload) {
    if (payload?.password !== STORAGE_WRITE_SECRET) {
      throw new Error('Wrong password: storage was not changed');
    }

    const key = this._key(payload.key);
    const current = await this._companyStorage.get(key);
    if (current === null || current === undefined) {
      return { key, deleted: false };
    }

    const previousHash = storageHash(this._export.toJsonSafe(current));
    if (payload.expectedHash && payload.expectedHash !== previousHash) {
      throw new Error(`"${key}" changed on the terminal after it was read: reload it and delete again`);
    }

    await this._companyStorage.remove(key);
    console.warn(`[storage] admin deleted "${key}"`);
    return { key, deleted: true, previousHash };
  }

  // The keys matching the pattern, read now, in the storage export's form (the admin's viewer
  // shows both alike).
  async read(payload: ReadStoragePayload) {
    const tenant = this._company();
    const source = typeof payload?.pattern === 'string' ? payload.pattern : '';
    if (source.length > PATTERN_MAX) {
      throw new Error('Key pattern is too long');
    }

    let pattern: RegExp | null = null;
    try {
      pattern = source ? new RegExp(source, 'i') : null;
    } catch {
      throw new Error(`Invalid key pattern: ${source}`);
    }

    const { document } = await this._export.read(tenant.clientid, tenant.name, (key) => !pattern || pattern.test(key));
    return document;
  }

  private _key(key: unknown): string {
    if (typeof key !== 'string' || !key.trim()) {
      throw new Error('Storage key is required');
    }

    this._company();
    return key;
  }

  // CompanyStorage alerts on the terminal screen without a company: refuse before touching it.
  private _company() {
    const tenant = this._tenantAuth.currentTenant;
    if (!tenant?.clientid) {
      throw new Error('No company is selected on this terminal: there is no company storage');
    }

    return tenant;
  }
}
